import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { HomePage } from './pages/HomePage';
import { SignInPage } from './pages/SignInPage';
import { FloorDetailPage } from './pages/FloorDetailPage';
import { HeatPumpPage } from './pages/HeatPumpPage';
import { FaultsPage } from './pages/FaultsPage';
import { TrendsPage } from './pages/TrendsPage';
import { AuditPage } from './pages/AuditPage';
import { SchedulesPage } from './pages/SchedulesPage';
import { SettingsPage } from './pages/SettingsPage';
import { AppShell } from './components/layout/AppShell';
import 'leaflet/dist/leaflet.css';
import './styles.css';

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;
if (!CLERK_PUBLISHABLE_KEY) {
  // Better to fail loudly than to silently render a broken sign-in.
  throw new Error('VITE_CLERK_PUBLISHABLE_KEY is not set');
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 1000,        // 5 s; the dashboard polls /state every 10 s
      refetchInterval: 10 * 1000,
      refetchOnWindowFocus: true,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/sign-in/*" element={<SignInPage />} />
            <Route
              element={
                <>
                  <SignedIn>
                    <AppShell>
                      <Outlet />
                    </AppShell>
                  </SignedIn>
                  <SignedOut>
                    <RedirectToSignIn />
                  </SignedOut>
                </>
              }
            >
              <Route path="/" element={<HomePage />} />
              <Route path="/floors/:floor" element={<FloorDetailPage />} />
              <Route path="/heat-pump" element={<HeatPumpPage />} />
              <Route path="/faults" element={<FaultsPage />} />
              <Route path="/trends" element={<TrendsPage />} />
              <Route path="/audit" element={<AuditPage />} />
              <Route path="/schedules" element={<SchedulesPage />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </ClerkProvider>
  </React.StrictMode>,
);
